import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Billboard, OrbitControls, Text } from '@react-three/drei';
import * as THREE from 'three';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { colors } from '../../const/colors';
import {
  getAllSlotCoords,
  getContainersInSlot,
  getSlotDisplayName,
  getSlotKey,
  isHidden,
  parseSlotKey,
  type SlotsState,
} from './slots';

const SLOT_W = 15;
const SLOT_D = 33;
const SLOT_H = 12;
const GAP = 3;
const ROAD_LENGTH = 12;
const ROAD_LINE_WIDTH = 0.6;
const ROAD_MARKING_LENGTH = 6;
const ROAD_MARKING_GAP = 5;
const ROAD_ELEVATION = 0.2;
const STEP_X_3D = SLOT_W + GAP;
const STEP_Z_3D = SLOT_D + GAP;
const DRAG_PREVIEW_SCALE = 1.06;
const FOCUSED_CAMERA_OFFSET = new THREE.Vector3(55, 42, 55);

type OrbitControlsLike = {
  target: THREE.Vector3;
  object: THREE.Camera;
  update: () => void;
  spherical?: THREE.Spherical;
};

function syncOrbitControls(
  camera: THREE.Camera,
  controls: unknown,
  targetVector: THREE.Vector3,
) {
  const orbitControls = controls as OrbitControlsLike | null;
  if (!orbitControls?.target) return;

  orbitControls.target.copy(targetVector);

  const offset = new THREE.Vector3().subVectors(camera.position, targetVector);
  if (orbitControls.spherical) {
    orbitControls.spherical.setFromVector3(offset);
  }

  orbitControls.update();
}

function FocusCamera({
  target,
  focusKey,
  onAnimatingChange,
}: {
  target?: [number, number, number];
  focusKey?: string;
  onAnimatingChange?: (animating: boolean) => void;
}) {
  const { camera, controls } = useThree();
  const focusedKeyRef = useRef<string>();
  const onAnimatingChangeRef = useRef(onAnimatingChange);
  onAnimatingChangeRef.current = onAnimatingChange;
  const animationRef = useRef<{
    elapsed: number;
    from: THREE.Vector3;
    to: THREE.Vector3;
    target: THREE.Vector3;
  }>();

  useEffect(() => {
    if (!target || !focusKey) {
      if (!focusKey) {
        focusedKeyRef.current = undefined;
        onAnimatingChangeRef.current?.(false);
      }
      return;
    }

    if (focusedKeyRef.current === focusKey) return;

    const targetVector = new THREE.Vector3(...target);
    focusedKeyRef.current = focusKey;
    onAnimatingChangeRef.current?.(true);
    animationRef.current = {
      elapsed: 0,
      from: camera.position.clone(),
      to: targetVector.clone().add(FOCUSED_CAMERA_OFFSET),
      target: targetVector,
    };
  }, [camera, focusKey, target?.[0], target?.[1], target?.[2]]);

  useFrame((_, delta) => {
    const animation = animationRef.current;
    if (!animation) return;

    animation.elapsed = Math.min(animation.elapsed + delta, 0.9);
    const progress = THREE.MathUtils.smoothstep(animation.elapsed / 0.9, 0, 1);
    camera.position.lerpVectors(animation.from, animation.to, progress);
    camera.lookAt(animation.target);

    if (progress === 1) {
      syncOrbitControls(camera, controls, animation.target);
      animationRef.current = undefined;
      onAnimatingChangeRef.current?.(false);
    }
  });

  return null;
}

type SceneItem = {
  type: 'railroad-tracks' | 'boat' | 'text-background' | 'road';
  position?: { x: number; y: number };
  rotate?: number;
  text?: string;
  width?: number;
  height?: number;
  length?: number;
  arrows?: boolean;
};

export type View3DProps = {
  state: SlotsState;
  selectedContainerNumber: string;
  selectedContainerFocusKey?: string;
  slotColor?: string;
  slotContainerColor?: string;
  containerSelectedColor?: string;
  selectedBlockId?: string;
  onSelectBlock?: (blockId?: string) => void;
  isBlocksEditing?: boolean;
  isContainersEditing?: boolean;
  onMoveContainer?: (containerId: string, targetSlotKey: string) => void;
  onEmptySlotSelect?: (slotKey: string) => void;
  selectedEmptySlotKey?: string;
  onContainerClick?: (containerId: string) => void;
  items?: SceneItem[];
  selectedItemIndex?: number;
  onSelectItem?: (itemIndex: number) => void;
  backgroundColor?: string;
  onHoverSlotChange?: (payload?: { slotKey: string; x: number; y: number }) => void;
  onHoverContainerChange?: (payload?: { containerId: string; x: number; y: number }) => void;
};

type CrossBlockMove = {
  containerId: string;
  sourceSlotKeys: string[];
  sourceTier: number;
};

const STACK_GAP = 1.5;
const SLOT_FLOOR_Y = 1;
const SLOT_LABEL_OFFSET = 0.4;
const CONTAINER_LIFT = 1.5;
const BLOCK_NAME_FONT_SIZE = 28;
const BLOCK_NAME_HEIGHT = 45;

const getBlockNameFromMapId = (blockMapId: string) => blockMapId.replace(/-\d+$/, '');

const getContainerY = (tier: number) =>
  SLOT_H / 2 + (tier - 1) * (SLOT_H + STACK_GAP) + CONTAINER_LIFT;

const getSlotLabelY = (topTier: number) => {
  if (topTier <= 0) {
    return SLOT_FLOOR_Y + SLOT_LABEL_OFFSET;
  }

  return getContainerY(topTier) + SLOT_H / 2 + SLOT_LABEL_OFFSET;
};

const getPlaceholderY = (topTier: number) =>
  topTier === 0 ? getContainerY(1) : SLOT_H / 2 + topTier * (SLOT_H + STACK_GAP) + CONTAINER_LIFT;

const View3DWrapper = styled.div<{ $background?: string }>`
  width: 100%;
  min-height: 0;
  height: 100%;
  background: ${(props) => props.$background ?? 'var(--gray-30)'};
  border-radius: 0.8rem;
  overflow: hidden;
`;

function BoatHull({ color }: { color: string }) {
  const geometry = useMemo(() => {
    const top = [
      [0, 5, -30],
      [-7, 5, -22],
      [-10, 5, 15],
      [-8, 5, 28],
      [8, 5, 28],
      [10, 5, 15],
      [7, 5, -22],
    ];
    const bottom = [
      [0, 0, -24],
      [-4, 0, -18],
      [-6, 0, 14],
      [-5, 0, 24],
      [5, 0, 24],
      [6, 0, 14],
      [4, 0, -18],
    ];
    const vertices = new Float32Array([...top, ...bottom].flat());
    const indices: number[] = [];

    for (let index = 0; index < top.length; index++) {
      const nextIndex = (index + 1) % top.length;
      indices.push(
        index,
        nextIndex,
        top.length + nextIndex,
        index,
        top.length + nextIndex,
        top.length + index,
      );
    }
    for (let index = 1; index < top.length - 1; index++) {
      indices.push(0, index, index + 1);
      indices.push(top.length, top.length + index + 1, top.length + index);
    }

    const hullGeometry = new THREE.BufferGeometry();
    hullGeometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    hullGeometry.setIndex(indices);
    hullGeometry.computeVertexNormals();
    return hullGeometry;
  }, []);

  useEffect(() => () => geometry.dispose(), [geometry]);

  return (
    <mesh geometry={geometry}>
      <meshStandardMaterial color={color} metalness={0.2} roughness={0.4} />
    </mesh>
  );
}

function BoatCabin() {
  const geometry = useMemo(() => {
    const vertices = new Float32Array([
      -5.5, 0, -7, 5.5, 0, -7, 5.5, 0, 7, -5.5, 0, 7, -4.5, 3.5, -4, 4.5, 3.5, -4, 4.5, 3.5, 6,
      -4.5, 3.5, 6,
    ]);
    const indices = [
      0, 1, 2, 0, 2, 3, 4, 6, 5, 4, 7, 6, 0, 4, 5, 0, 5, 1, 1, 5, 6, 1, 6, 2, 2, 6, 7, 2, 7, 3, 3,
      7, 4, 3, 4, 0,
    ];
    const cabinGeometry = new THREE.BufferGeometry();
    cabinGeometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    cabinGeometry.setIndex(indices);
    cabinGeometry.computeVertexNormals();
    return cabinGeometry;
  }, []);

  useEffect(() => () => geometry.dispose(), [geometry]);

  return (
    <mesh geometry={geometry}>
      <meshStandardMaterial color={colors['--white']} roughness={0.6} />
    </mesh>
  );
}

function RoadArrow({ position, size }: { position: [number, number, number]; size: number }) {
  const shape = useMemo(() => {
    const arrowShape = new THREE.Shape();
    arrowShape.moveTo(-0.22, -0.5);
    arrowShape.lineTo(0.22, -0.5);
    arrowShape.lineTo(0.22, 0.05);
    arrowShape.lineTo(0.48, 0.05);
    arrowShape.lineTo(0, 0.5);
    arrowShape.lineTo(-0.48, 0.05);
    arrowShape.lineTo(-0.22, 0.05);
    arrowShape.closePath();
    return arrowShape;
  }, []);

  return (
    <mesh position={position} rotation={[-Math.PI / 2, 0, 0]} scale={[size, size, size]}>
      <shapeGeometry args={[shape]} />
      <meshBasicMaterial color={colors['--white']} />
    </mesh>
  );
}

function SceneItems({
  items,
  layoutOrigin,
  fallbackPosition,
  selectedItemIndex,
  onSelectItem,
}: {
  items: SceneItem[];
  layoutOrigin: { col: number; row: number };
  fallbackPosition: { col: number; row: number };
  selectedItemIndex?: number;
  onSelectItem?: (itemIndex: number) => void;
}) {
  return (
    <>
      {items.map((item, index) => {
        const position = item.position ?? {
          x: fallbackPosition.col + index * 3,
          y: fallbackPosition.row,
        };
        const x = (position.x - layoutOrigin.col) * STEP_X_3D;
        const z = (position.y - layoutOrigin.row) * STEP_Z_3D;
        const rotation: [number, number, number] = [
          0,
          THREE.MathUtils.degToRad(item.rotate ?? 0),
          0,
        ];

        if (item.type === 'road') {
          const roadWidthUnits = Math.max(1, Math.round(item.width ?? 1));
          const roadWidth = roadWidthUnits * STEP_X_3D;
          const roadLength = (item.length ?? 2) * STEP_Z_3D;
          const markingLength = Math.min(ROAD_MARKING_LENGTH, roadLength / 4);
          const markingStep = markingLength + ROAD_MARKING_GAP;
          const roadMarkingsCount = Math.max(1, Math.floor(roadLength / markingStep));
          const roadMarkings = Array.from(
            { length: roadMarkingsCount },
            (_, markingIndex) => (markingIndex - (roadMarkingsCount - 1) / 2) * markingStep,
          );
          const arrowRowsCount = Math.max(1, Math.floor(item.length ?? 2));
          const arrowOffsets = Array.from(
            { length: arrowRowsCount },
            (_, arrowIndex) =>
              ((arrowIndex + 1) * roadLength) / (arrowRowsCount + 1) - roadLength / 2,
          );
          const arrowLaneOffsets = Array.from(
            { length: roadWidthUnits },
            (_, laneIndex) => ((laneIndex + 0.5) * roadWidth) / roadWidthUnits - roadWidth / 2,
          );
          const arrowSize = Math.min(
            STEP_X_3D * 0.45,
            (roadLength / (arrowRowsCount + 1)) * 0.45,
            8,
          );

          return (
            <group
              key={`${item.type}-${index}`}
              position={[x, 0, z]}
              rotation={rotation}
              onClick={
                onSelectItem
                  ? (event) => {
                      event.stopPropagation();
                      onSelectItem(index);
                    }
                  : undefined
              }
            >
              {selectedItemIndex === index && (
                <mesh position={[0, 0.08, 0]}>
                  <boxGeometry args={[roadWidth + 2, 0.2, roadLength + 2]} />
                  <meshStandardMaterial color={colors['--primary-100']} roughness={0.8} />
                </mesh>
              )}
              <mesh position={[0, 0.22, 0]}>
                <boxGeometry args={[roadWidth, 0.28, roadLength]} />
                <meshStandardMaterial color={colors['--gray-100']} roughness={0.95} />
              </mesh>
              {roadMarkings.map((offset) => (
                <mesh key={offset} position={[0, 0.52, offset]}>
                  <boxGeometry args={[ROAD_LINE_WIDTH, 0.08, markingLength]} />
                  <meshBasicMaterial color={colors['--yellow-100']} />
                </mesh>
              ))}
              {[-1, 1].map((side) => (
                <mesh key={side} position={[(side * (roadWidth - ROAD_LINE_WIDTH)) / 2, 0.4, 0]}>
                  <boxGeometry args={[ROAD_LINE_WIDTH, 0.08, roadLength]} />
                  <meshBasicMaterial color={colors['--white']} />
                </mesh>
              ))}
              {item.arrows && (
                <>
                  {arrowOffsets.flatMap((arrowOffset, arrowIndex) =>
                    arrowLaneOffsets.map((laneOffset, laneIndex) => (
                      <RoadArrow
                        key={`${arrowIndex}-${laneIndex}`}
                        position={[laneOffset, 0.8, arrowOffset]}
                        size={arrowSize}
                      />
                    )),
                  )}
                </>
              )}
            </group>
          );
        }

        if (item.type === 'text-background') {
          const width = (item.width ?? 3) * STEP_X_3D;
          const height = (item.height ?? 1) * STEP_Z_3D;
          const text = item.text ?? '';
          const fontSize = Math.max(
            0.8,
            Math.min(height * 0.42, width / Math.max(text.length * 0.62, 2)),
          );

          return (
            <group
              key={`${item.type}-${index}`}
              position={[x, 0, z]}
              rotation={rotation}
              onClick={
                onSelectItem
                  ? (event) => {
                      event.stopPropagation();
                      onSelectItem(index);
                    }
                  : undefined
              }
            >
              {selectedItemIndex === index && (
                <mesh position={[0, 0.08, 0]}>
                  <boxGeometry args={[width + 5, 0.2, height + 5]} />
                  <meshStandardMaterial color={colors['--primary-100']} roughness={0.7} />
                </mesh>
              )}
              <mesh position={[0, 0.2, 0]}>
                <boxGeometry args={[width + 3, 0.24, height + 3]} />
                <meshStandardMaterial color={colors['--gray-100']} roughness={0.75} />
              </mesh>
              <mesh position={[0, 0.46, 0]}>
                <boxGeometry args={[width, 0.3, height]} />
                <meshStandardMaterial color={colors['--white']} roughness={0.85} />
              </mesh>
              {text && (
                <Text
                  position={[0, 0.8, 0]}
                  rotation={[-Math.PI / 2, 0, 0]}
                  fontSize={fontSize}
                  maxWidth={width * 0.9}
                  color={colors['--black']}
                  anchorX="center"
                  anchorY="middle"
                  textAlign="center"
                  lineHeight={0.9}
                  renderOrder={2}
                >
                  {text}
                </Text>
              )}
            </group>
          );
        }

        if (item.type === 'railroad-tracks') {
          return (
            <group
              key={`${item.type}-${index}`}
              position={[x, 0, z]}
              rotation={rotation}
              scale={selectedItemIndex === index ? 1.08 : 1}
              onClick={
                onSelectItem
                  ? (event) => {
                      event.stopPropagation();
                      onSelectItem(index);
                    }
                  : undefined
              }
            >
              {[-5, 5].map((railOffset) => (
                <mesh key={railOffset} position={[railOffset, 0.4, 0]}>
                  <boxGeometry args={[1.2, 0.8, 72]} />
                  <meshStandardMaterial
                    color={colors['--gray-100']}
                    metalness={0.75}
                    roughness={0.35}
                  />
                </mesh>
              ))}
              {Array.from({ length: 9 }, (_, sleeperIndex) => (
                <mesh key={sleeperIndex} position={[0, 0.05, -32 + sleeperIndex * 8]}>
                  <boxGeometry args={[16, 0.6, 1.8]} />
                  <meshStandardMaterial color={colors['--gray-70']} roughness={0.9} />
                </mesh>
              ))}
            </group>
          );
        }

        return (
          <group
            key={`${item.type}-${index}`}
            position={[x, 0, z]}
            rotation={rotation}
            scale={selectedItemIndex === index ? 1.08 : 1}
            onClick={
              onSelectItem
                ? (event) => {
                    event.stopPropagation();
                    onSelectItem(index);
                  }
                : undefined
            }
          >
            <BoatHull color={colors['--red-90']} />
            <group position={[0, 5.1, 0]} scale={[0.93, 0.12, 0.9]}>
              <BoatHull color={colors['--white']} />
            </group>
            {[-10.1, 10.1].map((stripeX) => (
              <mesh key={stripeX} position={[stripeX, 3.8, 1]}>
                <boxGeometry args={[0.35, 1.5, 38]} />
                <meshStandardMaterial color={colors['--primary-70']} roughness={0.4} />
              </mesh>
            ))}
            <group position={[0, 5.3, 7]}>
              <BoatCabin />
            </group>
            <mesh position={[0, 9.05, 8]}>
              <boxGeometry args={[12, 0.5, 16]} />
              <meshStandardMaterial color={colors['--white']} metalness={0.1} roughness={0.45} />
            </mesh>
            <mesh position={[0, 8.72, 8]}>
              <boxGeometry args={[12.3, 0.15, 16.3]} />
              <meshStandardMaterial color={colors['--red-80']} metalness={0.15} roughness={0.4} />
            </mesh>
            {[-4, 4].map((windowX) => (
              <mesh key={windowX} position={[windowX, 7.4, -0.15]}>
                <boxGeometry args={[3, 2.2, 0.3]} />
                <meshStandardMaterial
                  color={colors['--gray-100']}
                  metalness={0.8}
                  roughness={0.2}
                />
              </mesh>
            ))}
            {[-1, 1].flatMap((side) =>
              [3, 10].map((windowZ) => (
                <mesh
                  key={`${side}-${windowZ}`}
                  position={[side * 5.6, 7.4, windowZ]}
                  rotation={[0, (side * Math.PI) / 2, 0]}
                >
                  <boxGeometry args={[3, 2.2, 0.3]} />
                  <meshStandardMaterial
                    color={colors['--gray-100']}
                    metalness={0.8}
                    roughness={0.2}
                  />
                </mesh>
              )),
            )}
            <mesh position={[0, 6.6, -18]}>
              <cylinderGeometry args={[0.7, 0.9, 1.6, 12]} />
              <meshStandardMaterial
                color={colors['--yellow-100']}
                metalness={0.5}
                roughness={0.3}
              />
            </mesh>
          </group>
        );
      })}
    </>
  );
}

function ContainerStack({
  state,
  col,
  row,
  gridColIndex,
  gridRowIndex,
  colsCount,
  rowsCount,
  selectedContainerNumber,
  slotColor,
  slotContainerColor,
  containerSelectedColor,
  onContainerClick,
}: {
  state: SlotsState;
  col: number;
  row: number;
  gridColIndex: number;
  gridRowIndex: number;
  colsCount: number;
  rowsCount: number;
  selectedContainerNumber: string;
  slotColor?: string;
  slotContainerColor?: string;
  containerSelectedColor?: string;
  onContainerClick?: (containerId: string) => void;
}) {
  const containers = getContainersInSlot(state, col, row);
  if (!containers.length) return null;
  const used = containers.length > 0;
  const centerCol = (colsCount - 1) / 2;
  const centerRow = (rowsCount - 1) / 2;
  const x = (gridColIndex - centerCol) * STEP_X_3D;
  const z = (gridRowIndex - centerRow) * STEP_Z_3D;

  return (
    <>
      {containers.map((container, i) => {
        let color: string;
        const isSelected =
          !!selectedContainerNumber && container.number === selectedContainerNumber;

        if (isSelected) {
          color = containerSelectedColor ?? colors['--green-100'];
        } else if (used) {
          color = slotContainerColor ?? colors['--primary-60'];
        } else {
          color = slotColor ?? colors['--gray-80'];
        }

        const y = getContainerY(i + 1);

        const sideOffset = SLOT_W / 2 + 0.1;

        return (
          <group key={container.id} position={[x, 0, z]} rotation={[0, 0, 0]}>
            <mesh
              position={[0, y, 0]}
              onClick={(event) => {
                if (event.delta > 2) return;
                event.stopPropagation();
                onContainerClick?.(container.id);
              }}
            >
              <boxGeometry args={[SLOT_W, SLOT_H, SLOT_D]} />
              <meshStandardMaterial
                color={color}
                metalness={0.15}
                roughness={0.6}
                envMapIntensity={1}
              />
            </mesh>

            <Text
              position={[-sideOffset, y, 0]}
              rotation={[0, -Math.PI / 2, 0]}
              fontSize={3}
              color={colors['--yellow-100']}
              anchorX="center"
              anchorY="middle"
              outlineWidth={0.15}
              outlineColor={colors['--black']}
              onClick={(event) => {
                if (event.delta > 2) return;
                event.stopPropagation();
                onContainerClick?.(container.id);
              }}
            >
              {container.number}
            </Text>

            <Text
              position={[sideOffset, y, 0]}
              rotation={[0, Math.PI / 2, 0]}
              fontSize={3}
              color={colors['--yellow-100']}
              anchorX="center"
              anchorY="middle"
              outlineWidth={0.15}
              outlineColor={colors['--black']}
              onClick={(event) => {
                if (event.delta > 2) return;
                event.stopPropagation();
                onContainerClick?.(container.id);
              }}
            >
              {container.number}
            </Text>
          </group>
        );
      })}
    </>
  );
}

function BlockContainers({
  blockMap,
  state,
  selectedContainerNumber,
  hoveredContainerId,
  onHoverContainer,
  isContainersEditing,
  onStartDragging,
  onContainerClick,
  hiddenContainerId,
}: {
  blockMap: NonNullable<SlotsState['blockMaps']>[number];
  state: SlotsState;
  selectedContainerNumber: string;
  hoveredContainerId?: string;
  onHoverContainer?: (containerId?: string, clientX?: number, clientY?: number) => void;
  isContainersEditing?: boolean;
  onStartDragging?: (containerId: string) => void;
  onContainerClick?: (containerId: string) => void;
  hiddenContainerId?: string;
}) {
  const containerPositionGroupRefs = useRef(new Map<string, THREE.Group>());
  const lastPositionGroupInstancesRef = useRef(new Map<string, THREE.Group>());
  const containerGroupRefs = useRef(new Map<string, THREE.Group>());
  const lastHeightGroupInstancesRef = useRef(new Map<string, THREE.Group>());
  const targetPositionByContainerRef = useRef(new Map<string, [number, number]>());
  const initializedPositionIdsRef = useRef(new Set<string>());
  const targetYByContainerRef = useRef(new Map<string, number>());
  const initializedContainerIdsRef = useRef(new Set<string>());
  const containers = state.containers.filter(
    (container) =>
      container.id !== hiddenContainerId &&
      (container.slotKeys ?? [container.slotKey]).some((slotKey) => {
        const { col, row } = parseSlotKey(slotKey);
        return (
          col >= blockMap.startCol &&
          col < blockMap.startCol + blockMap.colsCount &&
          row >= blockMap.startRow &&
          row < blockMap.startRow + blockMap.rowsCount
        );
      }),
  );

  useFrame((_, delta) => {
    targetPositionByContainerRef.current.forEach(([targetX, targetZ], containerId) => {
      const group = containerPositionGroupRefs.current.get(containerId);
      if (!group) return;

      if (!initializedPositionIdsRef.current.has(containerId)) {
        group.position.x = targetX;
        group.position.z = targetZ;
        initializedPositionIdsRef.current.add(containerId);
        return;
      }

      group.position.x = THREE.MathUtils.damp(group.position.x, targetX, 7, delta);
      group.position.z = THREE.MathUtils.damp(group.position.z, targetZ, 7, delta);
    });

    targetYByContainerRef.current.forEach((targetY, containerId) => {
      const group = containerGroupRefs.current.get(containerId);
      if (!group) return;

      if (!initializedContainerIdsRef.current.has(containerId)) {
        group.position.y = targetY;
        initializedContainerIdsRef.current.add(containerId);
        return;
      }

      group.position.y = THREE.MathUtils.damp(group.position.y, targetY, 7, delta);
    });
  });

  return (
    <>
      {containers.map((container) => {
        const slotCoords = (container.slotKeys ?? [container.slotKey]).map(parseSlotKey);
        const minCol = Math.min(...slotCoords.map((slot) => slot.col));
        const maxCol = Math.max(...slotCoords.map((slot) => slot.col));
        const minRow = Math.min(...slotCoords.map((slot) => slot.row));
        const maxRow = Math.max(...slotCoords.map((slot) => slot.row));
        const width = (maxCol - minCol) * STEP_X_3D + SLOT_W;
        const depth = (maxRow - minRow) * STEP_Z_3D + SLOT_D;
        const x =
          (minCol + (maxCol - minCol) / 2 - blockMap.startCol - (blockMap.colsCount - 1) / 2) *
          STEP_X_3D;
        const z =
          (minRow + (maxRow - minRow) / 2 - blockMap.startRow - (blockMap.rowsCount - 1) / 2) *
          STEP_Z_3D;
        const y = getContainerY(container.tier);
        const sideOffset = width / 2 + 0.1;
        const isHovered = container.id === hoveredContainerId;
        const isSelected =
          !!selectedContainerNumber && container.number === selectedContainerNumber;
        targetPositionByContainerRef.current.set(container.id, [x, z]);
        targetYByContainerRef.current.set(container.id, y);

        return (
          <group
            key={container.id}
            ref={(group) => {
              if (group) {
                containerPositionGroupRefs.current.set(container.id, group);
                if (lastPositionGroupInstancesRef.current.get(container.id) !== group) {
                  group.position.x = x;
                  group.position.z = z;
                  lastPositionGroupInstancesRef.current.set(container.id, group);
                  initializedPositionIdsRef.current.add(container.id);
                }
              } else {
                containerPositionGroupRefs.current.delete(container.id);
              }
            }}
          >
            <group
              ref={(group) => {
                if (group) {
                  containerGroupRefs.current.set(container.id, group);
                  if (lastHeightGroupInstancesRef.current.get(container.id) !== group) {
                    group.position.y = y;
                    lastHeightGroupInstancesRef.current.set(container.id, group);
                    initializedContainerIdsRef.current.add(container.id);
                  }
                } else {
                  containerGroupRefs.current.delete(container.id);
                }
              }}
            >
              <mesh
                onPointerOver={(event) => {
                  event.stopPropagation();
                  onHoverContainer?.(container.id, event.clientX, event.clientY);
                }}
                onPointerMove={(event) => {
                  event.stopPropagation();
                  onHoverContainer?.(container.id, event.clientX, event.clientY);
                }}
                onPointerOut={(event) => {
                  event.stopPropagation();
                  onHoverContainer?.();
                }}
                onPointerDown={(event) => {
                  if (!isContainersEditing) return;
                  event.stopPropagation();
                  onStartDragging?.(container.id);
                }}
                onClick={(event) => {
                  if (event.delta > 2) return;
                  event.stopPropagation();
                  onContainerClick?.(container.id);
                }}
              >
                <boxGeometry args={[width, SLOT_H, depth]} />
                <meshStandardMaterial
                  color={
                    isSelected
                      ? colors['--green-100']
                      : isHovered
                      ? colors['--yellow-100']
                      : colors['--primary-100']
                  }
                  emissive={isSelected ? colors['--green-100'] : colors['--black']}
                  emissiveIntensity={isSelected ? 0.45 : 0}
                  metalness={0.15}
                  roughness={0.6}
                  envMapIntensity={1}
                />
              </mesh>
              <Text
                position={[-sideOffset, 0, 0]}
                rotation={[0, -Math.PI / 2, 0]}
                fontSize={3}
                color={colors['--yellow-100']}
                anchorX="center"
                anchorY="middle"
                outlineWidth={0.15}
                outlineColor={colors['--black']}
                onClick={(event) => {
                  if (event.delta > 2) return;
                  event.stopPropagation();
                  onContainerClick?.(container.id);
                }}
              >
                {container.number}
              </Text>
              <Text
                position={[sideOffset, 0, 0]}
                rotation={[0, Math.PI / 2, 0]}
                fontSize={3}
                color={colors['--yellow-100']}
                anchorX="center"
                anchorY="middle"
                outlineWidth={0.15}
                outlineColor={colors['--black']}
                onClick={(event) => {
                  if (event.delta > 2) return;
                  event.stopPropagation();
                  onContainerClick?.(container.id);
                }}
              >
                {container.number}
              </Text>
            </group>
          </group>
        );
      })}
    </>
  );
}

type BlockMapProps = {
  blockMap: NonNullable<SlotsState['blockMaps']>[number];
  state: SlotsState;
  selectedContainerNumber: string;
  position: [number, number, number];
  selected: boolean;
  onSelect?: (blockId?: string) => void;
  isBlocksEditing?: boolean;
  isContainersEditing?: boolean;
  hoveredContainerId?: string;
  hoveredSlotKey?: string;
  selectedEmptySlotKey?: string;
  onHoverContainer?: (containerId?: string, clientX?: number, clientY?: number) => void;
  onHoverSlot?: (slotKey?: string, clientX?: number, clientY?: number) => void;
  onEmptySlotSelect?: (slotKey: string) => void;
  onContainerClick?: (containerId: string) => void;
  onStartDraggingContainer?: (containerId: string) => void;
  isContainerDragging?: boolean;
  draggingContainerId?: string;
  crossBlockMove?: CrossBlockMove;
};

function DraggingContainerPreview({
  blockMap,
  state,
  containerId,
  targetSlotKey,
}: {
  blockMap: NonNullable<SlotsState['blockMaps']>[number];
  state: SlotsState;
  containerId?: string;
  targetSlotKey?: string;
}) {
  const groupRef = useRef<THREE.Group>(null);
  const isInitializedRef = useRef(false);
  const container = state.containers.find((item) => item.id === containerId);
  let x = 0;
  let y = 0;
  let z = 0;
  let width = SLOT_W;
  let depth = SLOT_D;
  let hasPreview = false;

  if (container && targetSlotKey) {
    const sourceSlotKeys = container.slotKeys ?? [container.slotKey];
    const sourceAnchor = parseSlotKey(sourceSlotKeys[0]);
    const targetAnchor = parseSlotKey(targetSlotKey);
    const targetSlots = sourceSlotKeys.map((slotKey) => {
      const source = parseSlotKey(slotKey);
      return {
        col: targetAnchor.col + source.col - sourceAnchor.col,
        row: targetAnchor.row + source.row - sourceAnchor.row,
      };
    });
    const isInsideBlock = targetSlots.every(
      ({ col, row }) =>
        col >= blockMap.startCol &&
        col < blockMap.startCol + blockMap.colsCount &&
        row >= blockMap.startRow &&
        row < blockMap.startRow + blockMap.rowsCount &&
        !!state.slotNames?.[getSlotKey(col, row)],
    );

    if (isInsideBlock) {
      const minCol = Math.min(...targetSlots.map((slot) => slot.col));
      const maxCol = Math.max(...targetSlots.map((slot) => slot.col));
      const minRow = Math.min(...targetSlots.map((slot) => slot.row));
      const maxRow = Math.max(...targetSlots.map((slot) => slot.row));
      width = (maxCol - minCol) * STEP_X_3D + SLOT_W;
      depth = (maxRow - minRow) * STEP_Z_3D + SLOT_D;
      x =
        (minCol + (maxCol - minCol) / 2 - blockMap.startCol - (blockMap.colsCount - 1) / 2) *
        STEP_X_3D;
      z =
        (minRow + (maxRow - minRow) / 2 - blockMap.startRow - (blockMap.rowsCount - 1) / 2) *
        STEP_Z_3D;
      const topTier = Math.max(
        0,
        ...targetSlots.map(({ col, row }) =>
          getContainersInSlot(state, col, row)
            .filter((item) => item.id !== container.id)
            .reduce((maxTier, item) => Math.max(maxTier, item.tier), 0),
        ),
      );
      y = getContainerY(topTier + 1);
      hasPreview = true;
    }
  }

  useFrame(({ clock }, delta) => {
    if (!groupRef.current || !hasPreview) return;

    if (!isInitializedRef.current) {
      groupRef.current.position.set(x, y, z);
      isInitializedRef.current = true;
      return;
    }

    groupRef.current.position.x = x;
    groupRef.current.position.z = z;
    groupRef.current.position.y = THREE.MathUtils.damp(
      groupRef.current.position.y,
      y + Math.sin(clock.elapsedTime * 5) * 0.5,
      16,
      delta,
    );
  });

  if (!hasPreview) return null;

  return (
    <group ref={groupRef}>
      <mesh raycast={() => undefined}>
        <boxGeometry
          args={[
            width * DRAG_PREVIEW_SCALE,
            SLOT_H * DRAG_PREVIEW_SCALE,
            depth * DRAG_PREVIEW_SCALE,
          ]}
        />
        <meshStandardMaterial
          color={colors['--green-100']}
          opacity={0.5}
          transparent
          depthWrite={false}
          emissive={colors['--green-100']}
          emissiveIntensity={0.35}
        />
      </mesh>
    </group>
  );
}

function BlockMap({
  blockMap,
  state,
  selectedContainerNumber,
  position,
  selected,
  onSelect,
  isBlocksEditing,
  isContainersEditing,
  hoveredContainerId,
  hoveredSlotKey,
  selectedEmptySlotKey,
  onHoverContainer,
  onHoverSlot,
  onEmptySlotSelect,
  onContainerClick,
  onStartDraggingContainer,
  isContainerDragging,
  draggingContainerId,
  crossBlockMove,
}: BlockMapProps) {
  const groupRef = useRef<THREE.Group>(null);
  const isFirstFrameRef = useRef(true);
  const targetRotation = THREE.MathUtils.degToRad(blockMap.rotation);
  const width = (blockMap.colsCount - 1) * STEP_X_3D + SLOT_W + GAP * 2;
  const depth = (blockMap.rowsCount - 1) * STEP_Z_3D + SLOT_D + GAP * 2;
  const roadSides = blockMap.roads ?? [];
  const getRoad = (side: NonNullable<typeof blockMap.roads>[number]) =>
    side === 'top'
      ? {
          position: [0, ROAD_ELEVATION, -depth / 2 - ROAD_LENGTH / 2] as [number, number, number],
          size: [width, ROAD_LENGTH] as [number, number],
        }
      : side === 'right'
      ? {
          position: [width / 2 + ROAD_LENGTH / 2, ROAD_ELEVATION, 0] as [number, number, number],
          size: [ROAD_LENGTH, depth] as [number, number],
        }
      : side === 'bottom'
      ? {
          position: [0, ROAD_ELEVATION, depth / 2 + ROAD_LENGTH / 2] as [number, number, number],
          size: [width, ROAD_LENGTH] as [number, number],
        }
      : {
          position: [-width / 2 - ROAD_LENGTH / 2, ROAD_ELEVATION, 0] as [number, number, number],
          size: [ROAD_LENGTH, depth] as [number, number],
        };

  useFrame((_, delta) => {
    if (!groupRef.current) return;

    if (isFirstFrameRef.current) {
      groupRef.current.position.set(...position);
      isFirstFrameRef.current = false;
    } else {
      groupRef.current.position.x = THREE.MathUtils.damp(
        groupRef.current.position.x,
        position[0],
        10,
        delta,
      );
      groupRef.current.position.z = THREE.MathUtils.damp(
        groupRef.current.position.z,
        position[2],
        10,
        delta,
      );
    }

    groupRef.current.rotation.y = THREE.MathUtils.damp(
      groupRef.current.rotation.y,
      targetRotation,
      8,
      delta,
    );
  });

  return (
    <group
      ref={groupRef}
      onClick={(event) => {
        event.stopPropagation();
        onSelect?.(blockMap.id);
      }}
    >
      {roadSides.map((roadSide) => {
        const road = getRoad(roadSide);
        const isHorizontalRoad = roadSide === 'top' || roadSide === 'bottom';
        const roadLength = isHorizontalRoad ? road.size[0] : road.size[1];
        const roadMarkings = Array.from(
          { length: Math.ceil(roadLength / (ROAD_MARKING_LENGTH + ROAD_MARKING_GAP)) },
          (_, index) =>
            -roadLength / 2 +
            ROAD_MARKING_LENGTH / 2 +
            index * (ROAD_MARKING_LENGTH + ROAD_MARKING_GAP),
        ).filter((offset) => Math.abs(offset) + ROAD_MARKING_LENGTH / 2 <= roadLength / 2);

        return (
          <group key={roadSide}>
            <mesh position={road.position} rotation={[-Math.PI / 2, 0, 0]}>
              <planeGeometry args={road.size} />
              <meshStandardMaterial color={colors['--gray-100']} roughness={0.9} metalness={0} />
            </mesh>
            {roadMarkings.map((offset) => (
              <mesh
                key={offset}
                position={[
                  road.position[0] + (isHorizontalRoad ? offset : 0),
                  ROAD_ELEVATION + 0.02,
                  road.position[2] + (isHorizontalRoad ? 0 : offset),
                ]}
                rotation={[-Math.PI / 2, 0, 0]}
              >
                <planeGeometry
                  args={
                    isHorizontalRoad
                      ? [ROAD_MARKING_LENGTH, ROAD_LINE_WIDTH]
                      : [ROAD_LINE_WIDTH, ROAD_MARKING_LENGTH]
                  }
                />
                <meshBasicMaterial color={colors['--yellow-100']} />
              </mesh>
            ))}
            {[-1, 1].map((side) => (
              <mesh
                key={side}
                position={[
                  road.position[0] + (isHorizontalRoad ? 0 : (side * road.size[0]) / 2),
                  ROAD_ELEVATION + 0.02,
                  road.position[2] + (isHorizontalRoad ? (side * road.size[1]) / 2 : 0),
                ]}
                rotation={[-Math.PI / 2, 0, 0]}
              >
                <planeGeometry
                  args={
                    isHorizontalRoad
                      ? [road.size[0], ROAD_LINE_WIDTH]
                      : [ROAD_LINE_WIDTH, road.size[1]]
                  }
                />
                <meshBasicMaterial color={colors['--white']} />
              </mesh>
            ))}
          </group>
        );
      })}
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[width, depth]} />
        <meshStandardMaterial
          color={selected ? colors['--primary-100'] : colors['--gray-90']}
          roughness={1}
          metalness={0}
        />
      </mesh>

      {isBlocksEditing && (
        <Billboard position={[0, BLOCK_NAME_HEIGHT, 0]}>
          <Text
            fontSize={BLOCK_NAME_FONT_SIZE}
            color={colors['--white']}
            anchorX="center"
            anchorY="middle"
            outlineWidth={0.75}
            outlineColor={colors['--black']}
            raycast={() => null}
          >
            {getBlockNameFromMapId(blockMap.id)}
          </Text>
        </Billboard>
      )}

      {Array.from({ length: blockMap.rowsCount }, (_, rowIndex) =>
        Array.from({ length: blockMap.colsCount }, (_, colIndex) => {
          const col = blockMap.startCol + colIndex;
          const row = blockMap.startRow + rowIndex;
          const key = getSlotKey(col, row);
          const containers = getContainersInSlot(state, col, row);
          const used = containers.length > 0;
          const x = (colIndex - (blockMap.colsCount - 1) / 2) * STEP_X_3D;
          const z = (rowIndex - (blockMap.rowsCount - 1) / 2) * STEP_Z_3D;
          const topTier = containers.reduce((tier, container) => Math.max(tier, container.tier), 0);
          const placeholderY = getPlaceholderY(topTier);
          const labelY = getSlotLabelY(topTier);

          return (
            <group key={key}>
              <mesh position={[x, SLOT_FLOOR_Y, z]} rotation={[-Math.PI / 2, 0, 0]}>
                <planeGeometry args={[SLOT_W, SLOT_D]} />
                <meshBasicMaterial
                  color={used ? colors['--primary-60'] : colors['--gray-80']}
                  opacity={0.7}
                  transparent
                />
              </mesh>
              <Text
                position={[x, labelY, z]}
                rotation={[-Math.PI / 2, 0, 0]}
                fontSize={3}
                color={colors['--white']}
                anchorX="center"
                anchorY="middle"
                outlineWidth={0.2}
                outlineColor={colors['--black']}
                renderOrder={2}
                material-depthTest={false}
              >
                {getSlotDisplayName(state, key)}
              </Text>
              {isContainersEditing && (
                <mesh
                  position={[x, placeholderY, z]}
                  onPointerDown={(event) => {
                    event.stopPropagation();
                  }}
                  onPointerOver={(event) => {
                    event.stopPropagation();
                    onHoverSlot?.(key, event.clientX, event.clientY);
                  }}
                  onPointerMove={(event) => {
                    event.stopPropagation();
                    onHoverSlot?.(key, event.clientX, event.clientY);
                  }}
                  onPointerOut={(event) => {
                    event.stopPropagation();
                    onHoverSlot?.();
                  }}
                  onClick={(event) => {
                    if (isContainerDragging) return;
                    event.stopPropagation();
                    onEmptySlotSelect?.(key);
                  }}
                >
                  <boxGeometry args={[SLOT_W, SLOT_H, SLOT_D]} />
                  <meshBasicMaterial
                    color={selectedEmptySlotKey === key ? colors['--green-100'] : colors['--white']}
                    opacity={
                      selectedEmptySlotKey === key ? 0.45 : hoveredSlotKey === key ? 0.32 : 0.1
                    }
                    transparent
                    depthWrite={false}
                  />
                </mesh>
              )}
            </group>
          );
        }),
      )}
      <BlockContainers
        blockMap={blockMap}
        state={state}
        selectedContainerNumber={selectedContainerNumber}
        hoveredContainerId={hoveredContainerId}
        onHoverContainer={onHoverContainer}
        isContainersEditing={isContainersEditing}
        onStartDragging={onStartDraggingContainer}
        onContainerClick={onContainerClick}
        hiddenContainerId={crossBlockMove?.containerId}
      />
      {isContainersEditing && isContainerDragging && (
        <DraggingContainerPreview
          blockMap={blockMap}
          state={state}
          containerId={draggingContainerId}
          targetSlotKey={hoveredSlotKey}
        />
      )}
    </group>
  );
}

function CrossBlockContainerAnimation({
  state,
  move,
  layoutOrigin,
  onComplete,
}: {
  state: SlotsState;
  move: CrossBlockMove;
  layoutOrigin: { col: number; row: number };
  onComplete: () => void;
}) {
  const groupRef = useRef<THREE.Group>(null);
  const startedAtRef = useRef<number>();
  const isCompletedRef = useRef(false);

  const container = state.containers.find((item) => item.id === move.containerId);
  if (!container || !state.blockMaps) return null;

  const getBlock = (slotKey: string) => {
    const { col, row } = parseSlotKey(slotKey);
    return state.blockMaps?.find(
      (blockMap) =>
        col >= blockMap.startCol &&
        col < blockMap.startCol + blockMap.colsCount &&
        row >= blockMap.startRow &&
        row < blockMap.startRow + blockMap.rowsCount,
    );
  };
  const getPlacement = (
    blockMap: NonNullable<SlotsState['blockMaps']>[number],
    slotKeys: string[],
    tier: number,
  ) => {
    const slots = slotKeys.map(parseSlotKey);
    const minCol = Math.min(...slots.map((slot) => slot.col));
    const maxCol = Math.max(...slots.map((slot) => slot.col));
    const minRow = Math.min(...slots.map((slot) => slot.row));
    const maxRow = Math.max(...slots.map((slot) => slot.row));
    const localX =
      (minCol + (maxCol - minCol) / 2 - blockMap.startCol - (blockMap.colsCount - 1) / 2) *
      STEP_X_3D;
    const localZ =
      (minRow + (maxRow - minRow) / 2 - blockMap.startRow - (blockMap.rowsCount - 1) / 2) *
      STEP_Z_3D;
    const rotation = THREE.MathUtils.degToRad(blockMap.rotation);
    const originX =
      (blockMap.startCol + (blockMap.colsCount - 1) / 2 - layoutOrigin.col) * STEP_X_3D;
    const originZ =
      (blockMap.startRow + (blockMap.rowsCount - 1) / 2 - layoutOrigin.row) * STEP_Z_3D;

    return {
      x: originX + localX * Math.cos(rotation) + localZ * Math.sin(rotation),
      y: getContainerY(tier),
      z: originZ - localX * Math.sin(rotation) + localZ * Math.cos(rotation),
      rotation,
      width: (maxCol - minCol) * STEP_X_3D + SLOT_W,
      depth: (maxRow - minRow) * STEP_Z_3D + SLOT_D,
    };
  };

  const sourceBlock = getBlock(move.sourceSlotKeys[0]);
  const destinationSlotKeys = container.slotKeys ?? [container.slotKey];
  const destinationBlock = getBlock(destinationSlotKeys[0]);
  if (!sourceBlock || !destinationBlock) return null;

  const source = getPlacement(sourceBlock, move.sourceSlotKeys, move.sourceTier);
  const destination = getPlacement(destinationBlock, destinationSlotKeys, container.tier);

  useFrame(({ clock }) => {
    if (!groupRef.current) return;
    if (!startedAtRef.current) startedAtRef.current = clock.elapsedTime;
    const elapsed = (clock.elapsedTime - startedAtRef.current) / 0.65;
    const progress = THREE.MathUtils.smoothstep(elapsed, 0, 1);

    groupRef.current.position.set(
      THREE.MathUtils.lerp(source.x, destination.x, progress),
      THREE.MathUtils.lerp(source.y, destination.y, progress),
      THREE.MathUtils.lerp(source.z, destination.z, progress),
    );
    groupRef.current.rotation.y = THREE.MathUtils.lerp(
      source.rotation,
      destination.rotation,
      progress,
    );
    if (elapsed >= 1 && !isCompletedRef.current) {
      isCompletedRef.current = true;
      window.requestAnimationFrame(onComplete);
    }
  });

  return (
    <group
      ref={groupRef}
      position={[source.x, source.y, source.z]}
      rotation={[0, source.rotation, 0]}
    >
      <mesh>
        <boxGeometry args={[source.width, SLOT_H, source.depth]} />
        <meshStandardMaterial color={colors['--primary-100']} metalness={0.15} roughness={0.6} />
      </mesh>
      <Text
        position={[-source.width / 2 - 0.1, 0, 0]}
        rotation={[0, -Math.PI / 2, 0]}
        fontSize={3}
        color={colors['--yellow-100']}
        anchorX="center"
        anchorY="middle"
        outlineWidth={0.15}
        outlineColor={colors['--black']}
      >
        {container.number}
      </Text>
      <Text
        position={[source.width / 2 + 0.1, 0, 0]}
        rotation={[0, Math.PI / 2, 0]}
        fontSize={3}
        color={colors['--yellow-100']}
        anchorX="center"
        anchorY="middle"
        outlineWidth={0.15}
        outlineColor={colors['--black']}
      >
        {container.number}
      </Text>
    </group>
  );
}

function BlockMapsScene({
  state,
  selectedContainerNumber,
  selectedContainerFocusKey,
  minCol,
  minRow,
  colsCount,
  rowsCount,
  items = [],
  selectedItemIndex,
  onSelectItem,
  selectedBlockId,
  onSelectBlock,
  isBlocksEditing,
  isContainersEditing,
  hoveredContainerId,
  hoveredSlotKey,
  selectedEmptySlotKey,
  onHoverContainer,
  onHoverSlot,
  onEmptySlotSelect,
  onContainerClick,
  onStartDraggingContainer,
  isContainerDragging,
  draggingContainerId,
  crossBlockMove,
  onCompleteCrossBlockMove,
}: {
  state: SlotsState;
  selectedContainerNumber: string;
  selectedContainerFocusKey?: string;
  minCol: number;
  minRow: number;
  colsCount: number;
  rowsCount: number;
  items?: SceneItem[];
  selectedItemIndex?: number;
  onSelectItem?: (itemIndex: number) => void;
  selectedBlockId?: string;
  onSelectBlock?: (blockId?: string) => void;
  isBlocksEditing?: boolean;
  isContainersEditing?: boolean;
  hoveredContainerId?: string;
  hoveredSlotKey?: string;
  selectedEmptySlotKey?: string;
  onHoverContainer?: (containerId?: string, clientX?: number, clientY?: number) => void;
  onHoverSlot?: (slotKey?: string, clientX?: number, clientY?: number) => void;
  onEmptySlotSelect?: (slotKey: string) => void;
  onContainerClick?: (containerId: string) => void;
  onStartDraggingContainer?: (containerId: string) => void;
  isContainerDragging?: boolean;
  draggingContainerId?: string;
  crossBlockMove?: CrossBlockMove;
  onCompleteCrossBlockMove?: () => void;
}) {
  const selectedBlock = state.blockMaps?.find((blockMap) => blockMap.id === selectedBlockId);
  const centerCol = (colsCount - 1) / 2;
  const centerRow = (rowsCount - 1) / 2;
  const layoutOriginRef = useRef<{ col: number; row: number } | undefined>(undefined);
  const [cameraTarget, setCameraTarget] = useState<[number, number, number]>([0, 0, 0]);
  const [isFocusAnimating, setIsFocusAnimating] = useState(false);
  const selectedTargetRef = useRef<[number, number, number] | undefined>(undefined);

  if (!layoutOriginRef.current) {
    layoutOriginRef.current = {
      col: minCol + centerCol,
      row: minRow + centerRow,
    };
  }

  const layoutOrigin = layoutOriginRef.current;
  const selectedContainer = state.containers.find(
    (container) => container.number === selectedContainerNumber,
  );
  const selectedContainerSlots = selectedContainer
    ? (selectedContainer.slotKeys ?? [selectedContainer.slotKey]).map(parseSlotKey)
    : [];
  const selectedContainerBlock = selectedContainerSlots.length
    ? state.blockMaps?.find(
        (blockMap) =>
          selectedContainerSlots[0].col >= blockMap.startCol &&
          selectedContainerSlots[0].col < blockMap.startCol + blockMap.colsCount &&
          selectedContainerSlots[0].row >= blockMap.startRow &&
          selectedContainerSlots[0].row < blockMap.startRow + blockMap.rowsCount,
      )
    : undefined;

  if (selectedContainer && selectedContainerBlock && selectedContainerSlots.length) {
    const minContainerCol = Math.min(...selectedContainerSlots.map((slot) => slot.col));
    const maxContainerCol = Math.max(...selectedContainerSlots.map((slot) => slot.col));
    const minContainerRow = Math.min(...selectedContainerSlots.map((slot) => slot.row));
    const maxContainerRow = Math.max(...selectedContainerSlots.map((slot) => slot.row));
    const localX =
      (minContainerCol +
        (maxContainerCol - minContainerCol) / 2 -
        selectedContainerBlock.startCol -
        (selectedContainerBlock.colsCount - 1) / 2) *
      STEP_X_3D;
    const localZ =
      (minContainerRow +
        (maxContainerRow - minContainerRow) / 2 -
        selectedContainerBlock.startRow -
        (selectedContainerBlock.rowsCount - 1) / 2) *
      STEP_Z_3D;
    const rotation = THREE.MathUtils.degToRad(selectedContainerBlock.rotation);
    const blockX =
      (selectedContainerBlock.startCol +
        (selectedContainerBlock.colsCount - 1) / 2 -
        layoutOrigin.col) *
      STEP_X_3D;
    const blockZ =
      (selectedContainerBlock.startRow +
        (selectedContainerBlock.rowsCount - 1) / 2 -
        layoutOrigin.row) *
      STEP_Z_3D;

    selectedTargetRef.current = [
      blockX + localX * Math.cos(rotation) + localZ * Math.sin(rotation),
      getContainerY(selectedContainer.tier),
      blockZ - localX * Math.sin(rotation) + localZ * Math.cos(rotation),
    ];
  } else {
    selectedTargetRef.current = selectedBlock
      ? [
          (selectedBlock.startCol + (selectedBlock.colsCount - 1) / 2 - layoutOrigin.col) *
            STEP_X_3D,
          0,
          (selectedBlock.startRow + (selectedBlock.rowsCount - 1) / 2 - layoutOrigin.row) *
            STEP_Z_3D,
        ]
      : undefined;
  }

  useEffect(() => {
    if (selectedTargetRef.current) {
      setCameraTarget(selectedTargetRef.current);
    }
  }, [selectedBlockId, selectedContainerNumber]);

  useEffect(() => {
    if (!selectedContainerFocusKey || !selectedTargetRef.current) return;

    setCameraTarget(selectedTargetRef.current);
  }, [selectedContainerFocusKey]);

  return (
    <>
      <ambientLight intensity={0.7} />
      <hemisphereLight args={[colors['--primary-40'], colors['--gray-100'], 0.8]} />
      <directionalLight position={[50, 80, 40]} intensity={1.6} />
      <directionalLight position={[-30, 50, -20]} intensity={0.6} color={colors['--primary-40']} />
      <FocusCamera
        target={
          selectedContainerFocusKey && selectedContainer
            ? selectedTargetRef.current
            : undefined
        }
        focusKey={selectedContainerFocusKey}
        onAnimatingChange={setIsFocusAnimating}
      />
      <SceneItems
        items={items}
        layoutOrigin={layoutOrigin}
        fallbackPosition={{ col: minCol + colsCount + 2, row: minRow }}
        selectedItemIndex={selectedItemIndex}
        onSelectItem={onSelectItem}
      />

      {state.blockMaps?.map((blockMap) => {
        const x = (blockMap.startCol + (blockMap.colsCount - 1) / 2 - layoutOrigin.col) * STEP_X_3D;
        const z = (blockMap.startRow + (blockMap.rowsCount - 1) / 2 - layoutOrigin.row) * STEP_Z_3D;

        return (
          <BlockMap
            key={blockMap.id}
            blockMap={blockMap}
            state={state}
            selectedContainerNumber={selectedContainerNumber}
            position={[x, 0, z]}
            selected={blockMap.id === selectedBlockId}
            onSelect={onSelectBlock}
            isBlocksEditing={isBlocksEditing}
            isContainersEditing={isContainersEditing}
            hoveredContainerId={hoveredContainerId}
            hoveredSlotKey={hoveredSlotKey}
            selectedEmptySlotKey={selectedEmptySlotKey}
            onHoverContainer={onHoverContainer}
            onHoverSlot={onHoverSlot}
            onEmptySlotSelect={onEmptySlotSelect}
            onContainerClick={onContainerClick}
            onStartDraggingContainer={onStartDraggingContainer}
            isContainerDragging={isContainerDragging}
            draggingContainerId={draggingContainerId}
            crossBlockMove={crossBlockMove}
          />
        );
      })}
      {crossBlockMove && (
        <CrossBlockContainerAnimation
          state={state}
          move={crossBlockMove}
          layoutOrigin={layoutOrigin}
          onComplete={() => onCompleteCrossBlockMove?.()}
        />
      )}

      <OrbitControls
        enablePan
        enableZoom
        minDistance={40}
        maxDistance={1000}
        maxPolarAngle={Math.PI / 2 - 0.1}
        target={cameraTarget}
        enabled={!isContainerDragging && !isFocusAnimating}
      />
    </>
  );
}

function Scene({
  state,
  selectedContainerNumber,
  selectedContainerFocusKey,
  slotColor,
  slotContainerColor,
  containerSelectedColor,
  items,
  selectedItemIndex,
  onSelectItem,
  selectedBlockId,
  onSelectBlock,
  isBlocksEditing,
  isContainersEditing,
  hoveredContainerId,
  hoveredSlotKey,
  selectedEmptySlotKey,
  onHoverContainer,
  onHoverSlot,
  onEmptySlotSelect,
  onContainerClick,
  onStartDraggingContainer,
  isContainerDragging,
  draggingContainerId,
  crossBlockMove,
  onCompleteCrossBlockMove,
}: {
  state: SlotsState;
  selectedContainerNumber: string;
  selectedContainerFocusKey?: string;
  slotColor?: string;
  slotContainerColor?: string;
  containerSelectedColor?: string;
  items?: SceneItem[];
  selectedItemIndex?: number;
  onSelectItem?: (itemIndex: number) => void;
  selectedBlockId?: string;
  onSelectBlock?: (blockId?: string) => void;
  isBlocksEditing?: boolean;
  isContainersEditing?: boolean;
  hoveredContainerId?: string;
  hoveredSlotKey?: string;
  selectedEmptySlotKey?: string;
  onHoverContainer?: (containerId?: string, clientX?: number, clientY?: number) => void;
  onHoverSlot?: (slotKey?: string, clientX?: number, clientY?: number) => void;
  onEmptySlotSelect?: (slotKey: string) => void;
  onContainerClick?: (containerId: string) => void;
  onStartDraggingContainer?: (containerId: string) => void;
  isContainerDragging?: boolean;
  draggingContainerId?: string;
  crossBlockMove?: CrossBlockMove;
  onCompleteCrossBlockMove?: () => void;
}) {
  const allCoords: { col: number; row: number }[] = [];
  const seen = new Set<string>();

  const pushUnique = (col: number, row: number) => {
    const key = `${col},${row}`;
    if (seen.has(key)) return;
    seen.add(key);
    allCoords.push({ col, row });
  };

  if (state.slotNames) {
    for (const slotKey of Object.keys(state.slotNames)) {
      if (isHidden(state, slotKey)) continue;
      const { col, row } = parseSlotKey(slotKey);
      pushUnique(col, row);
    }
  }

  if (!allCoords.length) {
    const coords = getAllSlotCoords(state);
    for (const { col, row } of coords) {
      pushUnique(col, row);
    }
  }

  if (!allCoords.length) {
    return null;
  }

  let minCol = allCoords[0].col;
  let maxCol = allCoords[0].col;
  let minRow = allCoords[0].row;
  let maxRow = allCoords[0].row;

  for (let i = 1; i < allCoords.length; i++) {
    const { col, row } = allCoords[i];
    if (col < minCol) minCol = col;
    if (col > maxCol) maxCol = col;
    if (row < minRow) minRow = row;
    if (row > maxRow) maxRow = row;
  }

  const colsCount = maxCol - minCol + 1;
  const rowsCount = maxRow - minRow + 1;

  if (state.blockMaps?.length) {
    return (
      <BlockMapsScene
        state={state}
        selectedContainerNumber={selectedContainerNumber}
        selectedContainerFocusKey={selectedContainerFocusKey}
        minCol={minCol}
        minRow={minRow}
        colsCount={colsCount}
        rowsCount={rowsCount}
        items={items}
        selectedItemIndex={selectedItemIndex}
        onSelectItem={onSelectItem}
        selectedBlockId={selectedBlockId}
        onSelectBlock={onSelectBlock}
        isBlocksEditing={isBlocksEditing}
        isContainersEditing={isContainersEditing}
        hoveredContainerId={hoveredContainerId}
        hoveredSlotKey={hoveredSlotKey}
        selectedEmptySlotKey={selectedEmptySlotKey}
        onHoverContainer={onHoverContainer}
        onHoverSlot={onHoverSlot}
        onEmptySlotSelect={onEmptySlotSelect}
        onContainerClick={onContainerClick}
        onStartDraggingContainer={onStartDraggingContainer}
        isContainerDragging={isContainerDragging}
        draggingContainerId={draggingContainerId}
        crossBlockMove={crossBlockMove}
        onCompleteCrossBlockMove={onCompleteCrossBlockMove}
      />
    );
  }

  let targetX = 0;
  let targetZ = 0;

  if (selectedContainerNumber) {
    const firstMatch = state.containers.find((c) => c.number === selectedContainerNumber);
    if (firstMatch) {
      const { col, row } = parseSlotKey(firstMatch.slotKey);
      const colIndex = col - minCol;
      const rowIndex = row - minRow;
      const centerCol = (colsCount - 1) / 2;
      const centerRow = (rowsCount - 1) / 2;
      targetX = (colIndex - centerCol) * STEP_X_3D;
      targetZ = (rowIndex - centerRow) * STEP_Z_3D;
    }
  }

  return (
    <>
      <ambientLight intensity={0.7} />
      <hemisphereLight args={[colors['--primary-40'], colors['--gray-100'], 0.8]} />
      <directionalLight position={[50, 80, 40]} intensity={1.6} />
      <directionalLight position={[-30, 50, -20]} intensity={0.6} color={colors['--primary-40']} />
      <FocusCamera
        target={
          selectedContainerFocusKey && selectedContainerNumber
            ? [targetX, getContainerY(1), targetZ]
            : undefined
        }
        focusKey={selectedContainerFocusKey}
      />

      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
        <planeGeometry args={[colsCount * STEP_X_3D + GAP * 2, rowsCount * STEP_Z_3D + GAP * 2]} />
        <meshStandardMaterial color={colors['--gray-90']} roughness={1} metalness={0} />
      </mesh>

      {Array.from({ length: rowsCount }, (_, rowIndex) =>
        Array.from({ length: colsCount }, (_, colIndex) => {
          const col = minCol + colIndex;
          const row = minRow + rowIndex;
          const key = getSlotKey(col, row);
          const coordKey = `${col},${row}`;
          if (!seen.has(coordKey)) {
            return null;
          }
          const containers = getContainersInSlot(state, col, row);
          const used = containers.length > 0;
          const topTier = containers.reduce((tier, container) => Math.max(tier, container.tier), 0);
          const centerCol = (colsCount - 1) / 2;
          const centerRow = (rowsCount - 1) / 2;
          const x = (colIndex - centerCol) * STEP_X_3D;
          const z = (rowIndex - centerRow) * STEP_Z_3D;
          const slotLabel = getSlotDisplayName(state, key);
          const labelY = getSlotLabelY(topTier);

          return (
            <group key={key}>
              <mesh
                position={[x, SLOT_FLOOR_Y, z]}
                rotation={[-Math.PI / 2, 0, 0]}
                onClick={(event) => {
                  if (isContainerDragging) return;
                  event.stopPropagation();
                  onEmptySlotSelect?.(key);
                }}
              >
                <planeGeometry args={[SLOT_W, SLOT_D]} />
                <meshBasicMaterial
                  color={
                    selectedEmptySlotKey === key
                      ? colors['--green-100']
                      : used
                      ? colors['--primary-60']
                      : colors['--gray-80']
                  }
                  opacity={0.7}
                  transparent
                />
              </mesh>

              <Text
                position={[x, labelY, z]}
                rotation={[-Math.PI / 2, 0, 0]}
                fontSize={3}
                color={colors['--white']}
                anchorX="center"
                anchorY="middle"
                outlineWidth={0.2}
                outlineColor={colors['--black']}
                renderOrder={2}
                material-depthTest={false}
              >
                {slotLabel}
              </Text>

              <ContainerStack
                state={state}
                col={col}
                row={row}
                gridColIndex={colIndex}
                gridRowIndex={rowIndex}
                colsCount={colsCount}
                rowsCount={rowsCount}
                selectedContainerNumber={selectedContainerNumber}
                slotColor={slotColor}
                slotContainerColor={slotContainerColor}
                containerSelectedColor={containerSelectedColor}
                onContainerClick={onContainerClick}
              />
            </group>
          );
        }),
      )}

      <OrbitControls
        enablePan
        enableZoom
        minDistance={40}
        maxDistance={1000}
        maxPolarAngle={Math.PI / 2 - 0.1}
        target={[targetX, 0, targetZ]}
      />
    </>
  );
}

export function View3D({
  state,
  selectedContainerNumber,
  selectedContainerFocusKey,
  slotColor,
  slotContainerColor,
  containerSelectedColor,
  items,
  selectedItemIndex,
  onSelectItem,
  selectedBlockId,
  onSelectBlock,
  isBlocksEditing,
  isContainersEditing,
  onMoveContainer,
  onEmptySlotSelect,
  selectedEmptySlotKey,
  onContainerClick,
  backgroundColor,
  onHoverSlotChange,
  onHoverContainerChange,
}: View3DProps) {
  const [hoveredContainerId, setHoveredContainerId] = useState<string>();
  const [hoveredSlotKey, setHoveredSlotKey] = useState<string>();
  const [draggingContainerId, setDraggingContainerId] = useState<string>();
  const [crossBlockMove, setCrossBlockMove] = useState<CrossBlockMove>();
  const canvasRef = useRef<HTMLCanvasElement>();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const setSceneCursor = (isPointer: boolean) => {
    if (canvasRef.current) {
      canvasRef.current.style.cursor = isPointer ? 'pointer' : 'default';
    }
  };

  const handleHoverContainer = (containerId?: string, clientX?: number, clientY?: number) => {
    setHoveredContainerId(containerId);
    setSceneCursor(Boolean(containerId));

    if (!containerId || clientX == null || clientY == null) {
      onHoverContainerChange?.();
      return;
    }

    const rect = wrapperRef.current?.getBoundingClientRect();
    onHoverContainerChange?.({
      containerId,
      x: rect ? clientX - rect.left : clientX,
      y: rect ? clientY - rect.top : clientY,
    });
  };

  const handleHoverSlot = (slotKey?: string, clientX?: number, clientY?: number) => {
    setHoveredSlotKey(slotKey);
    setSceneCursor(Boolean(slotKey));

    if (!slotKey || clientX == null || clientY == null) {
      onHoverSlotChange?.();
      return;
    }

    const rect = wrapperRef.current?.getBoundingClientRect();
    onHoverSlotChange?.({
      slotKey,
      x: rect ? clientX - rect.left : clientX,
      y: rect ? clientY - rect.top : clientY,
    });
  };

  const handleDropContainer = () => {
    if (draggingContainerId && hoveredSlotKey) {
      const container = state.containers.find((item) => item.id === draggingContainerId);
      const sourceBlock = state.blockMaps?.find((blockMap) => {
        const sourceSlotKey = container?.slotKeys?.[0] ?? container?.slotKey;
        const { col, row } = parseSlotKey(sourceSlotKey ?? '');
        return (
          col >= blockMap.startCol &&
          col < blockMap.startCol + blockMap.colsCount &&
          row >= blockMap.startRow &&
          row < blockMap.startRow + blockMap.rowsCount
        );
      });
      const targetBlock = state.blockMaps?.find((blockMap) => {
        const { col, row } = parseSlotKey(hoveredSlotKey);
        return (
          col >= blockMap.startCol &&
          col < blockMap.startCol + blockMap.colsCount &&
          row >= blockMap.startRow &&
          row < blockMap.startRow + blockMap.rowsCount
        );
      });

      if (container && sourceBlock?.id !== targetBlock?.id) {
        setCrossBlockMove({
          containerId: container.id,
          sourceSlotKeys: container.slotKeys ?? [container.slotKey],
          sourceTier: container.tier,
        });
      }
      onMoveContainer?.(draggingContainerId, hoveredSlotKey);
    }

    setDraggingContainerId(undefined);
  };

  return (
    <View3DWrapper ref={wrapperRef} $background={backgroundColor}>
      <Canvas
        camera={{
          position: [100, 75, 100],
          fov: 50,
          near: 0.5,
          far: 1000000,
        }}
        gl={{
          antialias: true,
          toneMapping: THREE.ACESFilmicToneMapping,
          toneMappingExposure: 1.4,
          outputColorSpace: THREE.SRGBColorSpace,
        }}
        onCreated={({ gl, scene }) => {
          canvasRef.current = gl.domElement;
          scene.background = new THREE.Color(backgroundColor ?? colors['--gray-90']);
        }}
        onPointerMissed={() => onSelectBlock?.()}
        onPointerUp={handleDropContainer}
      >
        <Scene
          state={state}
          selectedContainerNumber={selectedContainerNumber}
          selectedContainerFocusKey={selectedContainerFocusKey}
          slotColor={slotColor}
          slotContainerColor={slotContainerColor}
          containerSelectedColor={containerSelectedColor}
          items={items}
          selectedItemIndex={selectedItemIndex}
          onSelectItem={onSelectItem}
          selectedBlockId={selectedBlockId}
          onSelectBlock={onSelectBlock}
          isBlocksEditing={isBlocksEditing}
          isContainersEditing={isContainersEditing}
          hoveredContainerId={hoveredContainerId}
          hoveredSlotKey={hoveredSlotKey}
          selectedEmptySlotKey={selectedEmptySlotKey}
          onHoverContainer={handleHoverContainer}
          onHoverSlot={handleHoverSlot}
          onEmptySlotSelect={onEmptySlotSelect}
          onContainerClick={onContainerClick}
          onStartDraggingContainer={setDraggingContainerId}
          isContainerDragging={Boolean(draggingContainerId)}
          draggingContainerId={draggingContainerId}
          crossBlockMove={crossBlockMove}
          onCompleteCrossBlockMove={() => setCrossBlockMove(undefined)}
        />
      </Canvas>
    </View3DWrapper>
  );
}
